import "./ReplyFiles.css";
import { ChevronDown } from "lucide-react";
import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ScopedThreadRef } from "@amoreg/contracts";
import { replyFileBadge, replyFileLabel } from "../files/replyFileLabel";
import { ReplyFileActions } from "../files/ReplyFileActions";
import { useRightPanelStore } from "~/rightPanelStore";
import { REPLY_FILES_PREVIEW_LIMIT, replyFiles } from "./ReplyFiles.logic";

/** Always visible, including historical replies without a changed-files diff. */
export function ReplyFiles({
  text,
  cwd,
  threadRef,
  changedFiles,
}: {
  changedFiles?: ReadonlyArray<{ path: string; kind: string }> | undefined;
  text: string;
  cwd?: string | undefined;
  threadRef?: ScopedThreadRef | undefined;
}) {
  const files = useMemo(() => replyFiles(text, cwd, changedFiles), [text, cwd, changedFiles]);
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const list = useRef<HTMLUListElement>(null);
  const previousHeight = useRef<number | null>(null);
  const animation = useRef<Animation | null>(null);
  useLayoutEffect(() => {
    const from = previousHeight.current;
    previousHeight.current = null;
    animation.current?.cancel();
    const element = list.current;
    if (element && from !== null && element.animate) {
      animation.current = element.animate(
        [{ height: `${from}px` }, { height: `${element.getBoundingClientRect().height}px` }],
        { duration: 220, easing: "cubic-bezier(0.23, 1, 0.32, 1)" },
      );
    }
    return () => animation.current?.cancel();
  }, [expanded]);
  if (!threadRef || !cwd || files.length === 0) return null;
  const visible = expanded ? files : files.slice(0, REPLY_FILES_PREVIEW_LIMIT);
  const remaining = files.length - REPLY_FILES_PREVIEW_LIMIT;
  return (
    <section
      aria-label="Files in this reply"
      className="reply-files mt-4 overflow-hidden rounded-xl border border-border bg-muted/20"
    >
      <ul ref={list} id={listId} className="overflow-hidden divide-y divide-border">
        {visible.map((file) => (
          <li key={file.filePath} className="reply-file-row flex items-center gap-3 pr-4">
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-3 pl-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              aria-label={`Open ${file.basename}`}
              onClick={() =>
                useRightPanelStore.getState().openFile(threadRef, file.workspaceRelativePath!)
              }
            >
              <span
                aria-hidden
                className="reply-file-badge flex size-11 shrink-0 items-center justify-center rounded-lg"
                data-format={replyFileBadge(file.filePath).tone}
              >
                <span className="reply-file-document"><span>{replyFileBadge(file.filePath).label}</span></span>
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className="block truncate text-sm font-medium"
                  title={file.workspaceRelativePath!}
                >
                  {file.basename}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {replyFileLabel(file.filePath)}
                </span>
              </span>
            </button>
            <ReplyFileActions cwd={cwd} threadRef={threadRef} file={file} />
          </li>
        ))}
      </ul>
      {remaining > 0 ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={(event) => {
            if (event.detail && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
              previousHeight.current = list.current?.getBoundingClientRect().height ?? null;
            }
            setExpanded((value) => !value);
          }}
          className="reply-files-more flex w-full items-center justify-center gap-2 border-t border-border px-4 py-2.5 text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          {expanded ? "Show less" : `More (${remaining})`}
          <ChevronDown
            aria-hidden
            className={`reply-files-chevron size-3.5 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      ) : null}
    </section>
  );
}
